import React, {useContext, useEffect, useRef, useCallback} from 'react';
import {
  useLocation,
  Navigate,
  useNavigate,
  Route,
  Routes,
} from 'react-router-dom';
import {UserContext} from './context/UserProvider';
import {WebsiteContext} from './context/WebsiteProvider';
import ProtectedRoute from './routes/ProtectedRoute';
import LoginPage from './views/authentication/LoginPage';
import BootstrapPage from './views/authentication/BootstrapPage';
import EmailSubmitPage from './views/authentication/EmailSubmitPage';
import ResetPage from './views/authentication/ResetPage';
import Registration from './views/authentication/Registration';
import Maintenance from './views/errors/Maintenance';
import ServerError from './views/errors/ServerError';
import NotFound from './views/errors/NotFound';
import Unauthorized from './views/errors/Unauthorized';
import Vendors from './views/vendor/Vendors';
import Websites from './views/website/Websites';
import Settings from './views/settings/Settings';
import ProfilePage from './views/settings/ProfilePage';
import Preferences from './views/settings/Preferences';
import Notifications from './views/settings/Notifications';
import Accounts from './views/settings/Accounts';
import VendorTypes from './views/settings/Tables/VendorTypes';
import FeedTypes from './views/settings/Tables/FeedTypes';
import FileDetails from './views/settings/Tables/FileDetails';
import Resources from './views/settings/Tables/Resources';
import ArchivedTable from './views/settings/ArchivedTable';
import NotificationsPage from './views/general/NotificationsPage';
import Changelog from './views/general/Changelog';
import ModalHandler from './components/modals/ModalHandler';
import axios from 'axios';
import './colorblind.css';
import './index.css';

const userAxios = axios.create();

userAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function App() {
  const {closeModal} = useContext(WebsiteContext);
  const {token, logout, loggedIn, bootstrap} = useContext(UserContext);
  const location = useLocation();
  const inactivityTimeout = useRef(null);
  const navigate = useNavigate();

  const startInactivityTimeout = useCallback(() => {
    clearTimeout(inactivityTimeout.current);
    inactivityTimeout.current = setTimeout(
      () => {
        if (loggedIn && location.pathname !== '/login') {
          logout();
          closeModal();
          window.location.href = '/login';
        }
      },
      1000 * 60 * 60
    );
  }, [loggedIn, location.pathname, logout]);

  useEffect(() => {
    const handleActivity = () => {
      startInactivityTimeout();
    };
    if (loggedIn) {
      startInactivityTimeout();
    } else {
      clearTimeout(inactivityTimeout.current);
    }
    document.addEventListener('mousemove', handleActivity);
    document.addEventListener('keydown', handleActivity);
    return () => {
      clearTimeout(inactivityTimeout.current);
      document.removeEventListener('mousemove', handleActivity);
      document.removeEventListener('keydown', handleActivity);
    };
  }, [loggedIn, location.pathname, logout, startInactivityTimeout]);

  useEffect(() => {
    if (bootstrap && location.pathname !== '/bootstrap' && location.pathname !== '/registration') {
      window.location.href = '/bootstrap';
    } else if (token && loggedIn && location.pathname === '/') {
      navigate('/vendors');
    }
  }, [token, loggedIn, navigate, location.pathname, bootstrap]);

  return (
    <div>
      <Routes>
        <Route path="/" element={<Navigate to="/login"/>}/>
        <Route path="/login" element={<LoginPage/>}/>
        <Route path="/bootstrap" element={<BootstrapPage/>}/>
        <Route path="/reset" element={<EmailSubmitPage/>}/>
        <Route path="/forgotpassword" element={<ResetPage/>}/>
        <Route path="/registration" element={<Registration/>}/>
        <Route element={<ProtectedRoute token={token}/>}>
          <Route path="/vendors" element={<Vendors/>}/>
          <Route path="/vendors/:id" element={<><Vendors/><ModalHandler type="vendor"/></>}/>
          <Route path="/websites" element={<Websites/>}/>
          <Route path="/websites/:id" element={<><Websites/><ModalHandler type="website"/></>}/>
          <Route path="/notifications" element={<NotificationsPage/>}/>
          <Route path="/changelog" element={<Changelog/>}/>
          <Route path="/maintenance" element={<Maintenance/>}/>
          <Route path="/internal-error" element={<ServerError/>}/>
          <Route path="/not-found" element={<NotFound/>}/>
          <Route path="/unauthorized" element={<Unauthorized/>}/>
          <Route path="/settings" element={<Settings/>}/>
          <Route path="/profile" element={<ProfilePage/>}/>
          <Route path="/preferences" element={<Preferences/>}/>
          <Route path="/notification-settings" element={<Notifications/>}/>
          <Route path="/admin/accounts" element={<Accounts/>}/>
          <Route path="/admin/archived" element={<ArchivedTable/>}/>
          <Route path="/admin/vendor-types" element={<VendorTypes/>}/>
          <Route path="/admin/feed-types" element={<FeedTypes/>}/>
          <Route path="/admin/file-details" element={<FileDetails/>}/>
          <Route path="/admin/resources" element={<Resources/>}/>
        </Route>
        <Route path="*" element={<Navigate to="/not-found"/>}/>
      </Routes>
    </div>
  );
}

export default App;
